interface MuyiAppInfo {
  versionCode: string;
  versionName: string;
  platform: string;
  debug: boolean;
}

declare global {
  interface Window {
    __MUYI_APP__?: boolean;
    __MUYI_APP_VERSION__?: string;
    __MUYI_APP_VERSION_NAME__?: string;
    __MUYI_APP_PLATFORM__?: string;
    __MUYI_DEBUG_FAB__?: boolean;
    
    // Bridge functions called by Android X5 WebView
    muyiInitApp?: (infoStr: string) => void;
    muyiReceiveAppState?: (state: string) => void;
    muyiRequestUA?: () => void;
    muyiInjectKeyboardScroll?: () => void;
    muyiInjectDebugFab?: () => void;
    
    // Bridge for sending data back to X5
    messageHandlers?: {
      bridge: (json: string) => void;
    };
    
    // Existing uni bridge
    uni?: {
      postMessage: (data: { data: any[] }) => void;
    };
  }
}

export const initMuyiBridge = () => {
  // 1. muyiInitApp
  window.muyiInitApp = (infoStr: string) => {
    try {
      const info: MuyiAppInfo = JSON.parse(infoStr);
      window.__MUYI_APP__ = true;
      window.__MUYI_APP_VERSION__ = info.versionCode;
      window.__MUYI_APP_VERSION_NAME__ = info.versionName;
      window.__MUYI_APP_PLATFORM__ = info.platform;
      console.log('[muyi] App initialized:', info);
      window.dispatchEvent(new CustomEvent('muyiAppReady', { detail: info }));
    } catch (e) {
      console.error('[muyi] Failed to parse app info', e);
    }
  };

  // 2. muyiReceiveAppState
  window.muyiReceiveAppState = (state: string) => {
    console.log('[muyi] App state received:', state);
    window.dispatchEvent(new CustomEvent('muyiAppState', { detail: { state } }));
  };

  // 3. muyiRequestUA
  window.muyiRequestUA = () => {
    const ua = navigator.userAgent;
    const msg = { type: 'muyiDebugUA', ua };
    sendToApp(msg);
  };

  // 4. muyiInjectKeyboardScroll
  window.muyiInjectKeyboardScroll = () => {
    function scrollFocusedIntoView(){
      var el = document.activeElement;
      if(el && (el.tagName==='INPUT'||el.tagName==='TEXTAREA'||(el as HTMLElement).contentEditable==='true')){
        setTimeout(function(){
          el.scrollIntoView({behavior:'smooth',block:'center'});
        },150);
      }
    }
    document.addEventListener('focus',scrollFocusedIntoView,true);
    document.addEventListener('focusin',scrollFocusedIntoView,true);
    console.log('[muyi] Keyboard scroll handler injected');
  };

  // 5. muyiInjectDebugFab
  window.muyiInjectDebugFab = () => {
    if(window.__MUYI_DEBUG_FAB__) return;
    window.__MUYI_DEBUG_FAB__ = true;
    var b = document.createElement('div');
    b.id = '__muyi_dbg__';
    b.textContent = 'DBG';
    b.style.cssText = 'position:fixed;bottom:60px;right:20px;width:44px;height:44px;border-radius:22px;background:rgba(233,69,96,0.88);color:#fff;font-size:11px;font-weight:bold;display:flex;align-items:center;justify-content:center;z-index:2147483647;cursor:pointer;user-select:none;';
    b.addEventListener('click', function(){
      try {
        sendToApp({ type: 'muyiOpenDebug' });
      } catch(e) {
        var v = window.__MUYI_APP_VERSION_NAME__ || '?';
        var c = window.__MUYI_APP_VERSION__ || '?';
        alert('App: '+v+' ('+c+')\nUA: '+navigator.userAgent);
      }
    });
    document.body.appendChild(b);
    console.log('[muyi] Debug FAB injected');
  };
};

/**
 * Unified sender to App.
 * Handles both uni.postMessage (Standard/iOS) and messageHandlers.bridge (Android X5).
 */
export const sendToApp = (data: any) => {
  // Priority: X5 Bridge -> uni.postMessage
  if (window.messageHandlers && window.messageHandlers.bridge) {
    window.messageHandlers.bridge(JSON.stringify(data));
  } else if (window.uni) {
    window.uni.postMessage({ data: [data] });
  } else {
    console.warn('[muyi] No bridge found to send data:', data);
  }
};

// Auto-initialize
initMuyiBridge();
